/**
 * Survey API Service
 * Handles all survey-related API calls
 */

import { get, post } from '@/services/shared/client'

export type SurveyTrigger =
  | 'post_checkout'
  | 'post_builder_save'
  | 'post_invitation_publish'
  | 'general_modal'
  | 'manual'

export interface SurveyDisplayConfig {
  modalTitle?: string
  modalDescription?: string
  submitButtonText?: string
  successTitle?: string
  successMessage?: string
}

export interface ActiveSurvey {
  id: string
  title: string
  schema: Record<string, unknown>
  displayConfig: SurveyDisplayConfig | null
  rewardConfig: {
    enabled: boolean
    giftTeaser?: string
    description?: string
  } | null
}

export interface SubmitSurveyRequest {
  data: Record<string, unknown>
  displayName: string
  metadata?: {
    orderId?: string
    invitationId?: string
    triggerContext?: string
  }
}

export interface SubmitSurveyResult {
  responseId: string
  promoCode?: {
    code: string
    expiresAt?: string
    description?: string
  }
}

export interface HomepageReview {
  id: string
  displayName: string
  rating?: number
  title?: string
  body?: string
  isFeatured: boolean
  createdAt: string
}

export interface HomepageReviewsResponse {
  /** @deprecated Frontend controls the homepage testimonials section title. */
  sectionTitle: string
  reviews: HomepageReview[]
}

/**
 * Get the highest-priority active survey for the current user and trigger.
 * Backend returns { success, data: ActiveSurvey | null }
 */
export async function getActiveSurvey(
  trigger?: SurveyTrigger
): Promise<ActiveSurvey | null> {
  const params = trigger ? `?trigger=${trigger}` : ''
  const response = await get<{ success: boolean; data: ActiveSurvey | null }>(
    `/surveys/active${params}`
  )
  return response.data
}

/**
 * Submit a survey response.
 * Backend returns { success, message, data: SubmitSurveyResult }
 */
export async function submitSurveyResponse(
  surveyId: string,
  data: SubmitSurveyRequest
): Promise<SubmitSurveyResult> {
  const response = await post<{
    success: boolean
    message: string
    data: SubmitSurveyResult
  }>(`/surveys/${surveyId}/respond`, data)
  return response.data
}

/**
 * Get public homepage reviews.
 * Backend returns { success, data: HomepageReviewsResponse }
 */
export async function getHomepageReviews(
  limit: number = 10
): Promise<HomepageReviewsResponse> {
  const response = await get<{
    success: boolean
    data: HomepageReviewsResponse
  }>(`/surveys/homepage-reviews?limit=${limit}`, true)
  return response.data
}
